import { SalesActionsType } from 'api-hooks/general/model';
import { useComplete, useRevertComplete } from 'api-hooks/general/mutation';
import { SaleModel } from 'api-hooks/sale/common';
import { CheckCircleSVG, CloseSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import stylesColors from 'common/styles/styles.colors';
import DeleteButton from 'components/common/delete-button';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useSalesCompleteAction(
  type: SalesActionsType,
  data: SaleModel,
) {
  const { t } = useTranslation();

  const dialog = useDialog();
  const { can } = useAuthorization();

  const unauthorizedNotification = React.useCallback(() => {
    notification.error({
      message: t('common:unauthorized_action'),
    });
  }, [t]);

  const permission = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return {
          incomplete: can(AuthorizationRules.FlightSalesRevertComplete),
          complete: can(AuthorizationRules.FlightSalesComplete),
        };
      case 'hotel-sales':
        return {
          incomplete: can(AuthorizationRules.HotelSalesRevertComplete),
          complete: can(AuthorizationRules.HotelSalesComplete),
        };

      case 'cruise-sales':
        return {
          incomplete: can(AuthorizationRules.CruiseSalesRevertComplete),
          complete: can(AuthorizationRules.CruiseSalesComplete),
        };
      case 'document-sales':
        return {
          incomplete: false,
          complete: false,
        };
      case 'activity-sales':
        return {
          incomplete: can(AuthorizationRules.ActivitySalesRevertComplete),
          complete: can(AuthorizationRules.ActivitySalesComplete),
        };
      case 'product-sales':
        return {
          incomplete: can(AuthorizationRules.ProductSalesRevertComplete),
          complete: can(AuthorizationRules.ProductSalesComplete),
        };
      case 'tour-sales':
        return {
          incomplete: can(AuthorizationRules.TourSalesRevertComplete),
          complete: can(AuthorizationRules.TourSalesComplete),
        };
    }
  }, [can, type]);

  const incompletePermission = data.isCompleted && permission.incomplete;

  const completePermission = !data.isCompleted && permission.complete;

  const completeMutate = useComplete();
  const revertCompleteMutate = useRevertComplete();

  const onDismiss = useDismiss(type, data.id);

  const onRevertComplete = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? data.id;
      dialog.showConfirmation({
        message: t(
          'common:do_you_want_to_change_this_sales_status_to_incomplete',
        ),
        title: t('common:confirmation'),
        onPositiveAction: async (dismiss) => {
          try {
            dismiss();

            const result = await revertCompleteMutate.mutateAsync({
              id: _id,
              type,
            });
            onDismiss(currentId);

            notification.success({ message: result?.message });
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
      });
    },
    [data.id, dialog, onDismiss, revertCompleteMutate, t, type],
  );

  const onComplete = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? data.id;

      dialog.showConfirmation({
        message: t(
          'common:do_you_want_to_change_this_sales_status_to_complete',
        ),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            onClose();

            const result = await completeMutate.mutateAsync({
              id: _id,
              type,
            });
            onDismiss(_id);

            notification.success({ message: result?.message });
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [dialog, t, completeMutate, data.id, type, onDismiss],
  );

  const completeAction: PopoverMenuType = {
    label: t('common:complete'),
    onClick: onComplete,
    isAllowed: completePermission,
    isError: false,
    key: `complete-${data.id}`,
  };

  const incompleteAction: PopoverMenuType = {
    label: t('common:incomplete'),
    onClick: onRevertComplete,
    isAllowed: incompletePermission,
    isError: true,
    key: `incomplete-${data.id}`,
  };

  const completeComponent = completePermission && (
    <Button
      startEnhancer={(color, size) => (
        <CheckCircleSVG color={color} size={size} />
      )}
      onClick={() => {
        onComplete();
      }}
      isLoading={completeMutate.isLoading}
    >
      {t('common:complete')}
    </Button>
  );

  const incompleteComponent = incompletePermission && (
    <DeleteButton
      text={t('common:incomplete')}
      onClick={() => {
        onRevertComplete();
      }}
      icon={<CloseSVG color={stylesColors.redDefault} width={16} height={16} />}
      isLoading={revertCompleteMutate.isLoading}
    />
  );

  return {
    onRevertComplete,
    onComplete,
    unauthorizedNotification,
    onDismiss,
    permission,
    completeAction,
    incompleteAction,
    completeComponent,
    incompleteComponent,
    incompletePermission,
    completePermission,
  };
}
